import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: 'top' | 'bottom';
}

interface UseDropdownPositionOptions {
  estimatedHeight?: number;
  gap?: number;
}

const DEFAULT_ESTIMATED_HEIGHT = 240;
const DEFAULT_GAP = 4;

/**
 * Nearest scrollable ancestor (overflow-y auto/scroll/overlay) — the boundary
 * the dropdown flips within and is dismissed against (e.g. a modal body).
 * Returns null when the trigger is in no scroll container, so the caller falls
 * back to the viewport (behaviour identical to the pre-container-aware hook).
 */
function findScrollAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function useDropdownPosition(
  triggerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose?: () => void,
  options?: UseDropdownPositionOptions,
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Resolved once per open (see effect) so the per-scroll path doesn't re-walk
  // the DOM / re-run getComputedStyle on every scroll event.
  const boundaryElRef = useRef<HTMLElement | null>(null);

  const estimatedHeight = options?.estimatedHeight ?? DEFAULT_ESTIMATED_HEIGHT;
  const gap = options?.gap ?? DEFAULT_GAP;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    // Container-driven boundary: the nearest scroll ancestor (modal body, etc.)
    // intersected with the viewport. No scroll container → full viewport, so the
    // flip/dismiss behaviour is identical to the original viewport-only hook.
    const boundaryRect = boundaryElRef.current?.getBoundingClientRect();
    const boundaryTop = boundaryRect ? Math.max(boundaryRect.top, 0) : 0;
    const boundaryBottom = boundaryRect
      ? Math.min(boundaryRect.bottom, window.innerHeight)
      : window.innerHeight;

    // Dismiss once the trigger scrolls out of the boundary's visible band.
    if (rect.bottom < boundaryTop || rect.top > boundaryBottom) {
      setPosition(null);
      onCloseRef.current?.();
      return;
    }

    const spaceBelow = boundaryBottom - rect.bottom;
    const spaceAbove = rect.top - boundaryTop;
    const shouldFlip = spaceBelow < estimatedHeight + gap && spaceAbove > spaceBelow;

    setPosition({
      top: shouldFlip ? rect.top - gap : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      placement: shouldFlip ? 'top' : 'bottom',
    });
  }, [triggerRef, estimatedHeight, gap]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      boundaryElRef.current = null;
      return;
    }
    boundaryElRef.current = triggerRef.current ? findScrollAncestor(triggerRef.current) : null;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Follow LAYOUT changes, not just scroll/viewport ones: content growing
    // while the dropdown is open (e.g. a chip list gaining rows in a centered
    // modal) re-centers the container and moves the trigger — without this the
    // portaled dropdown stays at its stale coordinates. Observing the scroll
    // ancestor (modal body / panel; body as fallback) catches that. Safe from
    // observer loops: updatePosition only writes position state for the
    // portaled dropdown, never resizes the observed element.
    const observed = boundaryElRef.current ?? document.body;
    const resizeObserver = new ResizeObserver(() => updatePosition());
    resizeObserver.observe(observed);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      resizeObserver.disconnect();
    };
  }, [isOpen, updatePosition, triggerRef]);

  return position;
}
